/**
 * @file ClientSettingBenefits.js
 * @description This file contains the ClientSettingBenefits component, which is a reusable form for displaying and editing an client's benefit information.
 */

import { useState, useEffect } from 'react';
import {
    getClientOptions,
    createClientOption,
    updateClientOption,
    deleteClientOption,
    createAsset,
    fetchAssets,
} from '../../services/clientService';
import { searchIndividuals } from '../../services/individualService';
import Toast from '../ui/toasts/Toast';
import {
    SecurityAttributes,
    ClientOptionEnum,
    BenefitsViewModeEnum,
} from '../../utils/data/enums';
import { useAuthContext, useUploadContext } from '../../store/Contexts';
import RichTextEditor from '../new-portal-page/RichTextEditor';
import styles from '../assets/AssetLibrary.module.scss';
import AssetGridView from '../assets/components/AssetGridView';
import PaginationComponent from '../helpers/PaginationComponent';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import { isValidFileTypeAndSize } from '../../utils/validate';
import { FileDrop } from '../helpers/FileDrop';
import { uploadFile } from '../../services/containerService';
import {
    formatFileSize,
    getAudioLength,
    getFileCategory,
    getImageDimensions,
    getVideoRuntime,
    sanitizeFileName,
} from '../helpers/FileUtils';
import { InputField, SwitchField } from '../helpers/FormComponents';
import Tooltip from '../ui/tooltips/Tooltip';
import TagInput from '../helpers/TagInput';
import { SearchableDropdown } from '../helpers/SearchableDropdown';
import { getDefaultBenefitTextHtml } from '../../utils/utils';
import { getText } from '../../i18n';

const ClientSettingBenefits = () => {
    const [
        initialDisplayBenefitTextSetting,
        setInitialDisplayBenefitTextSetting,
    ] = useState({});
    const [initialBenefitTextSetting, setInitialBenefitTextSetting] = useState(
        {},
    );
    const [
        initialDisplayEmployeeCostsSetting,
        setInitialDisplayEmployeeCostsSetting,
    ] = useState({});
    const [
        initialDisplayEmployerCostsSetting,
        setInitialDisplayEmployerCostsSetting,
    ] = useState({});
    const [
        initialDisplayOutOfNetworkInformationSetting,
        setInitialDisplayOutOfNetworkInformationSetting,
    ] = useState({});
    const [
        initialDisplayBenefitsModuleSetting,
        setInitialDisplayBenefitsModuleSetting,
    ] = useState({});
    const [
        initialBenefitsModuleIndividualsSetting,
        setInitialBenefitsModuleIndividualsSetting,
    ] = useState({});
    const [
        initialIndividualBenefitsViewSetting,
        setInitialIndividualBenefitsViewSetting,
    ] = useState({});

    const [selectedDisplayBenefitText, setSelectedDisplayBenefitText] =
        useState();
    const [selectedBenefitText, setSelectedBenefitText] = useState();
    const [selectedDisplayEmployeeCosts, setSelectedDisplayEmployeeCosts] =
        useState();
    const [selectedDisplayEmployerCosts, setSelectedDisplayEmployerCosts] =
        useState();
    const [
        selectedDisplayOutOfNetworkInformation,
        setSelectedDisplayOutOfNetworkInformation,
    ] = useState();
    const [selectedDisplayBenefitsModule, setSelectedDisplayBenefitsModule] =
        useState();
    const [
        selectedBenefitsModuleIndividuals,
        setSelectedBenefitsModuleIndividuals,
    ] = useState();
    const [selectedIndividualBenefitsView, setSelectedIndividualBenefitsView] =
        useState();

    const [isOpen, setIsOpen] = useState(true);
    const { hasAttribute } = useAuthContext();
    const { enqueueUpload } = useUploadContext();

    const [file, setFile] = useState(null);
    const [isAddFromAssetsChecked, setIsAddFromAssetsChecked] = useState('');
    const [isUploadFromDeviceChecked, setIsUploadFromDeviceChecked] =
        useState('');
    const [imageAsset1, setImageAsset1] = useState({});
    const [imageAsset2, setImageAsset2] = useState({});
    const [imageAsset3, setImageAsset3] = useState({});
    const [imageAsset4, setImageAsset4] = useState({});
    const [videoAsset, setVideoAsset] = useState({});
    const [textAssets, setTextAssets] = useState([]);
    const [text, setText] = useState('');
    const [videoUrl, setVideoUrl] = useState('');
    const [asset, setAsset] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [assets, setAssets] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeCard, setActiveCard] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [individualsDropdownArray, setIndividualsDropdownArray] = useState(
        [],
    );
    const overlayBackground = {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        zIndex: 999,
        overflow: 'auto',
        boxSizing: 'border-box',
        backgroundColor: 'rgba(255, 255, 255, 0.56)',
        display: 'flex',
        padding: '20px',
        justifyContent: 'center',
    };

    const overlayContainer = {
        width: '620px',
        height: '740px',
        maxHeight: '100hv',
        overflow: 'auto',
        boxSizing: 'border-box',
        backgroundColor: 'white',
        padding: '20px',
    };

    const getOptions = async () => {
        try {
            const data = await getClientOptions(0, [
                ClientOptionEnum.DisplayBenefitText,
                ClientOptionEnum.BenefitText,
                ClientOptionEnum.DisplayEmployeeCosts,
                ClientOptionEnum.DisplayEmployerCosts,
                ClientOptionEnum.DisplayOutOfNetworkInformation,
                ClientOptionEnum.DisplayBenefitsModule,
                ClientOptionEnum.BenefitsModuleIndividuals,
                ClientOptionEnum.IndividualBenefitsView,
            ]);

            const displayBenefitTextObject = data.find(
                item => item.name === 'Display Benefit Text',
            );
            if (displayBenefitTextObject) {
                setInitialDisplayBenefitTextSetting(displayBenefitTextObject);
                setSelectedDisplayBenefitText(displayBenefitTextObject.value);
            } else {
                Toast.error(getText('msg_891d6ccb4018'));
            }

            const benefitTextObject = data.find(
                item => item.name === 'Benefit Text',
            );
            if (benefitTextObject) {
                setInitialBenefitTextSetting(benefitTextObject);
                setSelectedBenefitText(benefitTextObject.value);
            } else {
                Toast.error(getText('msg_96135385d0d7'));
            }

            const displayEmployeeCostsObject = data.find(
                item => item.name === 'Display Employee Costs',
            );
            if (displayEmployeeCostsObject) {
                setInitialDisplayEmployeeCostsSetting(
                    displayEmployeeCostsObject,
                );
                setSelectedDisplayEmployeeCosts(
                    displayEmployeeCostsObject.value,
                );
            } else {
                Toast.error(getText('msg_7834d909d338'));
            }

            const displayEmployerCostsObject = data.find(
                item => item.name === 'Display Employer Costs',
            );
            if (displayEmployerCostsObject) {
                setInitialDisplayEmployerCostsSetting(
                    displayEmployerCostsObject,
                );
                setSelectedDisplayEmployerCosts(
                    displayEmployerCostsObject.value,
                );
            } else {
                Toast.error(getText('msg_dd8481d8b3c3'));
            }

            const displayOutOfNetworkInformationObject = data.find(
                item => item.name === 'Display Out of Network Information',
            );
            if (displayOutOfNetworkInformationObject) {
                setInitialDisplayOutOfNetworkInformationSetting(
                    displayOutOfNetworkInformationObject,
                );
                setSelectedDisplayOutOfNetworkInformation(
                    displayOutOfNetworkInformationObject.value,
                );
            } else {
                Toast.error(getText('msg_0b249f92f03b'));
            }

            const displayBenefitsModuleObject = data.find(
                item => item.name === 'Display Benefits Module',
            );
            if (displayBenefitsModuleObject) {
                setInitialDisplayBenefitsModuleSetting(
                    displayBenefitsModuleObject,
                );
                setSelectedDisplayBenefitsModule(
                    displayBenefitsModuleObject.value,
                );
            } else {
                Toast.error(getText('msg_015d607be60e'));
            }

            const benefitsModuleIndividualsObject = data.find(
                item => item.name === 'Benefits Module Individuals',
            );
            if (benefitsModuleIndividualsObject) {
                setInitialBenefitsModuleIndividualsSetting(
                    benefitsModuleIndividualsObject,
                );
                setSelectedBenefitsModuleIndividuals(
                    JSON.parse(benefitsModuleIndividualsObject.value),
                );
            } else {
                Toast.error(getText('msg_ebbd7f1a1913'));
            }

            const individualBenefitsViewObject = data.find(
                item => item.name === 'Individual Benefits View',
            );
            if (individualBenefitsViewObject) {
                setInitialIndividualBenefitsViewSetting(
                    individualBenefitsViewObject,
                );
                setSelectedIndividualBenefitsView(
                    `${individualBenefitsViewObject.value}`,
                );
            } else {
                Toast.error(getText('msg_b4172c856c0c'));
            }
        } catch (error) {
            console.error('Could not fetch:', error);
        }
    };

    useEffect(() => {
        getOptions();

        searchIndividuals(null).then(response => {
            const formattedIndividuals = response.individuals.map(
                individual => ({
                    id: individual.id,
                    name: `${individual.firstName} ${individual.lastName}`,
                }),
            );
            setIndividualsDropdownArray(formattedIndividuals);
        });
    }, []);

    const toggleAccordion = () => {
        setIsOpen(!isOpen);
    };

    const hasBenefitText = html => {
        if (!html) return false;
        const textOnly = html
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/gi, ' ')
            .trim();
        return textOnly.length > 0;
    };

    const handleSave = async () => {
        let noChanges = true;

        if (initialDisplayBenefitTextSetting.usingDefaultValue) {
            //make a post
            noChanges = false;
            const option = {
                clientOptionId: initialDisplayBenefitTextSetting.clientOptionId,
                value: `${selectedDisplayBenefitText}`,
            };
            const response = await createClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_da1794823f5d'));
            setInitialDisplayBenefitTextSetting(response.data);
        } else if (
            initialDisplayBenefitTextSetting.value !=
            `${selectedDisplayBenefitText}`
        ) {
            //make a put
            noChanges = false;
            const option = {
                clientOptionId: initialDisplayBenefitTextSetting.clientOptionId,
                value: `${selectedDisplayBenefitText}`,
            };
            const response = await updateClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_4ca3821fb3cb'));
            setInitialDisplayBenefitTextSetting(response.data);
        }

        if (selectedBenefitText === null) {
        } else if (initialBenefitTextSetting.usingDefaultValue) {
            //make a post
            noChanges = false;
            const option = {
                clientOptionId: initialBenefitTextSetting.clientOptionId,
                value: `${selectedBenefitText}`,
            };
            const response = await createClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_3a1d993a536d'));
            setInitialBenefitTextSetting(response.data);
        } else if (
            initialBenefitTextSetting.value != `${selectedBenefitText}`
        ) {
            //make a put
            noChanges = false;
            const option = {
                clientOptionId: initialBenefitTextSetting.clientOptionId,
                value: `${selectedBenefitText}`,
            };
            const response = await updateClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_045996a7e7b5'));
            setInitialBenefitTextSetting(response.data);
        }

        if (initialDisplayEmployeeCostsSetting.usingDefaultValue) {
            //make a post
            noChanges = false;
            const option = {
                clientOptionId:
                    initialDisplayEmployeeCostsSetting.clientOptionId,
                value: `${selectedDisplayEmployeeCosts}`,
            };
            const response = await createClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_90813fcb7e37'));
            setInitialDisplayEmployeeCostsSetting(response.data);
        } else if (
            initialDisplayEmployeeCostsSetting.value !=
            `${selectedDisplayEmployeeCosts}`
        ) {
            //make a put
            noChanges = false;
            const option = {
                clientOptionId:
                    initialDisplayEmployeeCostsSetting.clientOptionId,
                value: `${selectedDisplayEmployeeCosts}`,
            };
            const response = await updateClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_c6b59898fe8f'));
            setInitialDisplayEmployeeCostsSetting(response.data);
        }

        if (initialDisplayEmployerCostsSetting.usingDefaultValue) {
            //make a post
            noChanges = false;
            const option = {
                clientOptionId:
                    initialDisplayEmployerCostsSetting.clientOptionId,
                value: `${selectedDisplayEmployerCosts}`,
            };
            const response = await createClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_f38d454e9b7e'));
            setInitialDisplayEmployerCostsSetting(response.data);
        } else if (
            initialDisplayEmployerCostsSetting.value !=
            `${selectedDisplayEmployerCosts}`
        ) {
            //make a put
            noChanges = false;
            const option = {
                clientOptionId:
                    initialDisplayEmployerCostsSetting.clientOptionId,
                value: `${selectedDisplayEmployerCosts}`,
            };
            const response = await updateClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_0262ef8099fa'));
            setInitialDisplayEmployerCostsSetting(response.data);
        }

        if (initialDisplayOutOfNetworkInformationSetting.usingDefaultValue) {
            //make a post
            noChanges = false;
            const option = {
                clientOptionId:
                    initialDisplayOutOfNetworkInformationSetting.clientOptionId,
                value: `${selectedDisplayOutOfNetworkInformation}`,
            };
            const response = await createClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_480de4b5596f'));
            setInitialDisplayOutOfNetworkInformationSetting(response.data);
        } else if (
            initialDisplayOutOfNetworkInformationSetting.value !=
            `${selectedDisplayOutOfNetworkInformation}`
        ) {
            //make a put
            noChanges = false;
            const option = {
                clientOptionId:
                    initialDisplayOutOfNetworkInformationSetting.clientOptionId,
                value: `${selectedDisplayOutOfNetworkInformation}`,
            };
            const response = await updateClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_8444724361e2'));
            setInitialDisplayOutOfNetworkInformationSetting(response.data);
        }

        if (initialDisplayBenefitsModuleSetting.usingDefaultValue) {
            //make a post
            noChanges = false;
            const option = {
                clientOptionId:
                    initialDisplayBenefitsModuleSetting.clientOptionId,
                value: `${selectedDisplayBenefitsModule}`,
            };
            const response = await createClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_ff90a1396801'));
            setInitialDisplayBenefitsModuleSetting(response.data);
        } else if (
            initialDisplayBenefitsModuleSetting.value !=
            `${selectedDisplayBenefitsModule}`
        ) {
            //make a put
            noChanges = false;
            const option = {
                clientOptionId:
                    initialDisplayBenefitsModuleSetting.clientOptionId,
                value: `${selectedDisplayBenefitsModule}`,
            };
            const response = await updateClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_dd33b4bafee5'));
            setInitialDisplayBenefitsModuleSetting(response.data);
        }

        if (initialIndividualBenefitsViewSetting.usingDefaultValue) {
            noChanges = false;
            const option = {
                clientOptionId:
                    initialIndividualBenefitsViewSetting.clientOptionId,
                value: `${selectedIndividualBenefitsView}`,
            };
            const response = await createClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_7b07d7f57d70'));
            setInitialIndividualBenefitsViewSetting(response.data);
        } else if (
            initialIndividualBenefitsViewSetting.value !=
            `${selectedIndividualBenefitsView}`
        ) {
            noChanges = false;
            const option = {
                clientOptionId:
                    initialIndividualBenefitsViewSetting.clientOptionId,
                value: `${selectedIndividualBenefitsView}`,
            };
            const response = await updateClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_e3507b82f5a4'));
            setInitialIndividualBenefitsViewSetting(response.data);
        }

        if (
            selectedDisplayBenefitsModule !== 'testMode' &&
            !initialBenefitsModuleIndividualsSetting.usingDefaultValue
        ) {
            //make a delete
            noChanges = false;
            const deleteResponse = await deleteClientOption(
                0,
                initialBenefitsModuleIndividualsSetting.clientOptionId,
            );

            if (deleteResponse.error)
                return Toast.error(getText('msg_cf9abd636fa2'));

            const data = await getClientOptions(0, [
                ClientOptionEnum.BenefitsModuleIndividuals,
            ]);

            const benefitsModuleIndividualsObject = data.find(
                item => item.name === 'Benefits Module Individuals',
            );
            if (benefitsModuleIndividualsObject) {
                setInitialBenefitsModuleIndividualsSetting(
                    benefitsModuleIndividualsObject,
                );
                setSelectedBenefitsModuleIndividuals(
                    JSON.parse(benefitsModuleIndividualsObject.value),
                );
            }
        }
        if (
            selectedDisplayBenefitsModule === 'testMode' &&
            initialBenefitsModuleIndividualsSetting.usingDefaultValue
        ) {
            //make a post
            noChanges = false;
            const option = {
                clientOptionId:
                    initialBenefitsModuleIndividualsSetting.clientOptionId,
                value: JSON.stringify(selectedBenefitsModuleIndividuals),
            };
            const response = await createClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_08bbd3110f41'));
            setInitialBenefitsModuleIndividualsSetting(response.data);
        } else if (
            selectedDisplayBenefitsModule === 'testMode' &&
            initialBenefitsModuleIndividualsSetting.value !=
                JSON.stringify(selectedBenefitsModuleIndividuals)
        ) {
            //make a put
            noChanges = false;
            const option = {
                clientOptionId:
                    initialBenefitsModuleIndividualsSetting.clientOptionId,
                value: JSON.stringify(selectedBenefitsModuleIndividuals),
            };
            const response = await updateClientOption(option, 0);
            if (response.error) return Toast.error(getText('msg_cf9abd636fa2'));
            setInitialBenefitsModuleIndividualsSetting(response.data);
        }

        if (noChanges) Toast.warning(getText('msg_13c3a7ac4e47'));
        else Toast.success(getText('msg_2a72676a1f5c'));
    };

    const handleCancel = () => {
        setSelectedDisplayBenefitText(initialDisplayBenefitTextSetting.value);
        setSelectedBenefitText(initialBenefitTextSetting.value);
        setSelectedDisplayEmployeeCosts(
            initialDisplayEmployeeCostsSetting.value,
        );
        setSelectedDisplayEmployerCosts(
            initialDisplayEmployerCostsSetting.value,
        );
        setSelectedDisplayOutOfNetworkInformation(
            initialDisplayOutOfNetworkInformationSetting.value,
        );
        setSelectedDisplayBenefitsModule(
            initialDisplayBenefitsModuleSetting.value,
        );
        setSelectedBenefitsModuleIndividuals(
            JSON.parse(initialBenefitsModuleIndividualsSetting.value),
        );
        setSelectedIndividualBenefitsView(
            `${initialIndividualBenefitsViewSetting.value}`,
        );
    };

    useEffect(() => {
        const updatedTextAssets = textAssets.filter(asset =>
            text.includes(asset.url),
        );
        setTextAssets(updatedTextAssets);
    }, [text]);

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets();
        }
    }, [isAddFromAssetsChecked]);

    const handleFileChange = fileValue => {
        setFile(sanitizeFileName(fileValue));
    };

    const uploadAsset = async () => {
        setIsSaving(true);

        if (!validateForm()) {
            setIsSaving(false);
            return;
        }

        let urlResponse;
        let isValidUrl = false;

        if (!asset.url) {
            try {
                const response = await enqueueUpload(file.name, uploadOptions =>
                    uploadFile(file, uploadOptions),
                );
                if (response.error) {
                    Toast.error(response.message);
                } else {
                    urlResponse = encodeURI(response);
                }
                try {
                    new URL(urlResponse);
                    isValidUrl = true;
                    setAsset(prev => ({
                        ...prev,
                        url: urlResponse,
                    }));
                } catch (error) {
                    Toast.error(getText('msg_3e298875de25'));
                }
            } catch (error) {
                Toast.error(getText('msg_3e298875de25'));
            }
        }

        if (isValidUrl || asset.url) {
            const newAsset = { ...asset };
            if (!newAsset.assetTypeId) newAsset.assetTypeId = 1;
            if (urlResponse) newAsset.url = urlResponse;
            newAsset.fileType = file.name.split('.').pop();
            newAsset.fileSize = formatFileSize(file.size);

            const fileCategory = getFileCategory(file.name);
            if (fileCategory === 'video') {
                newAsset.metaData = await getVideoRuntime(file);
            } else if (fileCategory === 'audio') {
                newAsset.metaData = await getAudioLength(file);
            } else if (fileCategory === 'image') {
                newAsset.metaData = await getImageDimensions(file);
            }

            const createResponse = await createAsset(newAsset, null);
            if (createResponse.error) {
                Toast.error(createResponse.message);
            } else if (
                createResponse.status === 200 &&
                createResponse.data.url
            ) {
                Toast.success(getText('msg_9309271e92fe'));
                switch (isUploadFromDeviceChecked) {
                    case 'text':
                        setTextAssets(prevTextAssets => {
                            // Only add the asset if it doesn't already exist

                            const assetExists = prevTextAssets.some(
                                asset =>
                                    asset.assetId === createResponse.data.id,
                            );
                            if (!assetExists) {
                                return [
                                    ...prevTextAssets,
                                    {
                                        assetId: createResponse.data.id,
                                        assetTypeId:
                                            createResponse.data.assetTypeId,
                                        url: createResponse.data.url,
                                    },
                                ];
                            }
                            return prevTextAssets; // Return the previous state if the asset already exists
                        });
                        break;
                    default:
                        break;
                }
                setIsUploadFromDeviceChecked('');
                setAsset({});
                setFile(null);
            } else {
                Toast.error(getText('msg_9f8fd8f97fd3'));
            }
        }

        setIsSaving(false);
    };

    const validateForm = () => {
        if (!asset.name || asset.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }

        if (!file) {
            Toast.error(getText('msg_e11c5988bd25'));
            return false;
        }

        const fileTypeAndSizeValidation = isValidFileTypeAndSize(file);
        if (fileTypeAndSizeValidation) {
            Toast.error(fileTypeAndSizeValidation);
            return false;
        }

        if (asset.name.length > 256) {
            Toast.error(getText('msg_f4231db74e51'));
            return false;
        }

        return true;
    };

    function updateAsset(asset, key, value) {
        return { ...asset, [key]: value };
    }

    const handleSearch = event => {
        setSearchTerm(event.target.value);
    };

    const loadAssets = async () => {
        try {
            let queryParams = {
                assetTypes: [1, 2],
                page: currentPage,
                pageSize: 12,
            };
            queryParams = {
                ...queryParams,
                fileTypes: ['jpg', 'jpeg', 'png', 'svg'],
            };
            if (searchTerm !== '' && searchTerm !== ' ') {
                queryParams = { ...queryParams, search: searchTerm };
            }
            const fetchedAssets = await fetchAssets(queryParams, null, false);
            setAssets(fetchedAssets.viewModels);
            setTotalPages(fetchedAssets.pageTotal);
        } catch (error) {
            console.error('Failed to fetch assets', error);
            Toast.error(getText('msg_cade149a85b2'));
        }
    };

    useEffect(() => {
        if (isAddFromAssetsChecked) {
            loadAssets();
        }
    }, [currentPage, searchTerm]);

    const handleClick = index => {
        setActiveCard(activeCard === index ? null : index);
    };

    const addAsset = () => {
        if (activeCard === null) {
            return Toast.error(
                getText('msg_c9d0d3fc9172'),
                null,
                getText('msg_40f0bdba0938'),
            );
        }

        const assetAdding = assets[activeCard];
        switch (isAddFromAssetsChecked) {
            case 'image': {
                const imageAssets = [
                    imageAsset1,
                    imageAsset2,
                    imageAsset3,
                    imageAsset4,
                ];
                const assetSetters = [
                    setImageAsset1,
                    setImageAsset2,
                    setImageAsset3,
                    setImageAsset4,
                ];

                // Find the first empty slot
                const emptyIndex = imageAssets.findIndex(asset => !asset.url);

                // Update the thumbnail and the internal image array
                if (emptyIndex !== -1) {
                    assetSetters[emptyIndex]({
                        assetId: assetAdding.id,
                        url: assetAdding.url,
                    });
                }
                break;
            }
            case 'video':
                setVideoAsset({
                    assetId: assetAdding.id,
                    url: assetAdding.url,
                });
                setVideoUrl('');
                break;
            case 'text':
                setTextAssets(prevTextAssets => {
                    // Only add the asset if it doesn't already exist

                    const assetExists = prevTextAssets.some(
                        asset => asset.assetId === assetAdding.id,
                    );
                    if (!assetExists) {
                        return [
                            ...prevTextAssets,
                            { assetId: assetAdding.id, url: assetAdding.url },
                        ];
                    }
                    return prevTextAssets; // Return the previous state if the asset already exists
                });
                break;
            default:
                break;
        }
        setIsAddFromAssetsChecked('');
        setSearchTerm('');
        setAssets([]);
        setTotalPages(1);
        setCurrentPage(1);
        setActiveCard(null);
    };

    const handleIndividualChange = selectedOptions => {
        const ids = selectedOptions.map(opt => opt.id);
        setSelectedBenefitsModuleIndividuals(ids);
    };

    return (
        <div
            style={{
                overflow: 'hidden',
                borderRadius: '12px',
                border: 'solid 1px rgba(47, 43, 67, 0.2)',
                marginTop: '10px',
                boxShadow:
                    'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0.1) 0px 1px 3px 0px',
            }}
        >
            <div
                style={{
                    overflowY: 'auto',
                    padding: '16px',
                    resize: 'none',
                    boxShadow:
                        'rgba(47, 43, 67, 0.1) 0px -1px 0px 0px inset, rgba(47, 43, 67, 0.1) 0px 1px 3px 0px;',
                }}
            >
                <div>
                    <div style={{ marginLeft: '12px' }}>
                        {getText('msg_927f40673764')}
                        <i
                            style={{
                                color: 'var(--iNGAGED-Neutral-Color3)',
                                display: 'flex',
                                float: 'inline-end',
                                cursor: 'pointer',
                            }}
                            className={
                                'bi bi-chevron-' + (isOpen ? 'up' : 'down')
                            }
                            onClick={() => toggleAccordion()}
                        />
                    </div>
                </div>
                {isOpen && (
                    <div
                        style={{
                            marginLeft: '48px',
                            marginRight: '48px',
                            marginTop: '12px',
                        }}
                    >
                        <div style={{ marginTop: '12px' }}>
                            {getText('msg_7d9ff4f0de39')}
                        </div>
                        <div
                            style={{
                                color: 'var(--iNGAGED-Neutral-Color2)',
                                marginBottom: '12px',
                            }}
                        >
                            {getText('msg_3239137e1af5')}
                        </div>
                        <div
                            className="form-check"
                            style={{ float: 'left', marginRight: '50px' }}
                        >
                            <input
                                className="form-check-input"
                                type="radio"
                                name="flexRadioEnabled"
                                id="flexRadioEnabled"
                                value="enabled"
                                checked={
                                    selectedDisplayBenefitsModule === 'enabled'
                                }
                                onChange={() =>
                                    setSelectedDisplayBenefitsModule('enabled')
                                }
                            />
                            <label className="form-check-label" htmlFor="url">
                                {getText('msg_df174a3f2faa')}
                            </label>
                        </div>
                        <div
                            className="form-check"
                            style={{ float: 'left', marginRight: '50px' }}
                        >
                            <input
                                className="form-check-input"
                                type="radio"
                                name="flexRadioDisabled"
                                id="flexRadioDisabled"
                                value="disabled"
                                checked={
                                    selectedDisplayBenefitsModule === 'disabled'
                                }
                                onChange={() =>
                                    setSelectedDisplayBenefitsModule('disabled')
                                }
                            />
                            <label
                                className="form-check-label"
                                htmlFor="upload"
                            >
                                {getText('msg_f4f4473df8cb')}
                            </label>
                        </div>
                        <div className="form-check" style={{ float: 'left' }}>
                            <input
                                className="form-check-input"
                                type="radio"
                                name="flexRadioTestMode"
                                id="flexRadioTestMode"
                                value="testMode"
                                checked={
                                    selectedDisplayBenefitsModule === 'testMode'
                                }
                                onChange={() =>
                                    setSelectedDisplayBenefitsModule('testMode')
                                }
                            />
                            <label
                                className="form-check-label"
                                htmlFor="upload"
                            >
                                {getText('msg_e9ef2fa6b328')}
                            </label>
                        </div>
                        <br />
                        <br />
                        {selectedDisplayBenefitsModule === 'testMode' && (
                            <>
                                <SearchableDropdown
                                    placeholderLabel={getText(
                                        'msg_8598222918d3',
                                    )}
                                    options={individualsDropdownArray}
                                    optionIDField="id"
                                    savedOptions={selectedBenefitsModuleIndividuals
                                        .map(id =>
                                            individualsDropdownArray.find(
                                                opt => opt.id === id,
                                            ),
                                        )
                                        .filter(Boolean)}
                                    savedOptionIDField="id"
                                    updateOptions={selectedOptions =>
                                        handleIndividualChange(selectedOptions)
                                    }
                                    disabled={false}
                                    showTags={true}
                                />
                                <br />
                            </>
                        )}

                        <div style={{ marginTop: '12px' }}>
                            {getText('msg_ed894c58c7a6')}
                        </div>
                        <div
                            style={{
                                color: 'var(--iNGAGED-Neutral-Color2)',
                                marginBottom: '12px',
                            }}
                        >
                            {getText('msg_58dbcf936573')}
                        </div>
                        <div
                            className="form-check"
                            style={{ float: 'left', marginRight: '50px' }}
                        >
                            <input
                                className="form-check-input"
                                type="radio"
                                name="individualBenefitsView"
                                id="individualBenefitsGridView"
                                value={`${BenefitsViewModeEnum.Grid}`}
                                checked={
                                    selectedIndividualBenefitsView ===
                                    `${BenefitsViewModeEnum.Grid}`
                                }
                                onChange={() =>
                                    setSelectedIndividualBenefitsView(
                                        `${BenefitsViewModeEnum.Grid}`,
                                    )
                                }
                            />
                            <label
                                className="form-check-label"
                                htmlFor="individualBenefitsGridView"
                            >
                                {getText('msg_602bf12902a7')}
                            </label>
                        </div>
                        <div
                            className="form-check"
                            style={{ float: 'left', marginRight: '50px' }}
                        >
                            <input
                                className="form-check-input"
                                type="radio"
                                name="individualBenefitsView"
                                id="individualBenefitsListView"
                                value={`${BenefitsViewModeEnum.List}`}
                                checked={
                                    selectedIndividualBenefitsView ===
                                    `${BenefitsViewModeEnum.List}`
                                }
                                onChange={() =>
                                    setSelectedIndividualBenefitsView(
                                        `${BenefitsViewModeEnum.List}`,
                                    )
                                }
                            />
                            <label
                                className="form-check-label"
                                htmlFor="individualBenefitsListView"
                            >
                                {getText('msg_fb1bde1b6c26')}
                            </label>
                        </div>
                        <br />
                        <br />

                        <div style={{ marginTop: '12px' }}>
                            {getText('msg_03fcc0bc0d08')}
                        </div>
                        <div
                            style={{
                                color: 'var(--iNGAGED-Neutral-Color2)',
                                marginBottom: '12px',
                            }}
                        >
                            {getText('msg_c31ea10b1269')}
                        </div>

                        <div style={{ marginLeft: '24px' }}>
                            {/* Display Benefit Text toggle hidden per request */}
                            {/* <div
                                style={{
                                    display: 'flex',
                                    gap: '5px',
                                    alignItems: 'anchor-center',
                                    marginTop: '12px',
                                    marginBottom: '12px',
                                }}
                            >
                                <div className={`form-switch`}>
                                    <input
                                        onClick={() =>
                                            setSelectedDisplayBenefitText(
                                                selectedDisplayBenefitText ===
                                                    'true'
                                                    ? 'false'
                                                    : 'true',
                                            )
                                        }
                                        className="form-check-input custom-switch-input"
                                        type="checkbox"
                                        checked={
                                            selectedDisplayBenefitText ===
                                            'true'
                                        }
                                    />
                                </div>
                                <div>{getText('msg_ccef955d6447')}</div>
                            </div> */}
                            <RichTextEditor
                                initialContent={
                                    hasBenefitText(
                                        initialBenefitTextSetting.value,
                                    )
                                        ? initialBenefitTextSetting.value
                                        : getDefaultBenefitTextHtml()
                                }
                                onContentUpdate={content =>
                                    setSelectedBenefitText(content)
                                }
                                onUploadFromDevice={
                                    setIsUploadFromDeviceChecked
                                }
                                onAddFromAssets={setIsAddFromAssetsChecked}
                                textAssets={textAssets}
                                onAssetDelete={null}
                                disableHomeLink={true}
                                placeholderText={getText('msg_25a243b14a2f')}
                            />
                        </div>

                        <div style={{ marginTop: '24px' }}>
                            {getText('msg_799b3e5c2304')}
                        </div>
                        <div
                            style={{
                                color: 'var(--iNGAGED-Neutral-Color2)',
                                marginBottom: '12px',
                            }}
                        >
                            {getText('msg_a545345b1821')}
                        </div>

                        <div style={{ marginLeft: '24px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '5px',
                                    alignItems: 'anchor-center',
                                    marginTop: '12px',
                                }}
                            >
                                <div className={`form-switch`}>
                                    <input
                                        onClick={() =>
                                            setSelectedDisplayEmployeeCosts(
                                                selectedDisplayEmployeeCosts ===
                                                    'true'
                                                    ? 'false'
                                                    : 'true',
                                            )
                                        }
                                        className="form-check-input custom-switch-input"
                                        type="checkbox"
                                        checked={
                                            selectedDisplayEmployeeCosts ===
                                            'true'
                                        }
                                    />
                                </div>
                                <div>{getText('msg_ccea3418a6ee')}</div>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '5px',
                                    alignItems: 'anchor-center',
                                    marginTop: '12px',
                                }}
                            >
                                <div className={`form-switch`}>
                                    <input
                                        onClick={() =>
                                            setSelectedDisplayEmployerCosts(
                                                selectedDisplayEmployerCosts ===
                                                    'true'
                                                    ? 'false'
                                                    : 'true',
                                            )
                                        }
                                        className="form-check-input custom-switch-input"
                                        type="checkbox"
                                        checked={
                                            selectedDisplayEmployerCosts ===
                                            'true'
                                        }
                                    />
                                </div>
                                <div>{getText('msg_75c47ace399b')}</div>
                            </div>
                        </div>

                        <div style={{ marginTop: '24px' }}>
                            {getText('msg_7a43cdd08459')}
                        </div>
                        <div
                            style={{
                                color: 'var(--iNGAGED-Neutral-Color2)',
                                marginBottom: '12px',
                            }}
                        >
                            {getText('msg_009845284557')}
                        </div>

                        <div style={{ marginLeft: '24px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '5px',
                                    alignItems: 'anchor-center',
                                    marginTop: '12px',
                                }}
                            >
                                <div className={`form-switch`}>
                                    <input
                                        onClick={() =>
                                            setSelectedDisplayOutOfNetworkInformation(
                                                selectedDisplayOutOfNetworkInformation ===
                                                    'true'
                                                    ? 'false'
                                                    : 'true',
                                            )
                                        }
                                        className="form-check-input custom-switch-input"
                                        type="checkbox"
                                        checked={
                                            selectedDisplayOutOfNetworkInformation ===
                                            'true'
                                        }
                                    />
                                </div>
                                <div>{getText('msg_bd10b72b94e1')}</div>
                            </div>
                        </div>

                        {hasAttribute(
                            SecurityAttributes.ClientSettingsFullAccess,
                        ) && (
                            <div
                                className={`d-flex justify-content-evenly mt-5`}
                            >
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="ing-popup-blue-negative"
                                >
                                    {getText('msg_77dfd2135f4d')}
                                </button>
                                <button
                                    type="button"
                                    className="ing-button-primary-none"
                                    onClick={handleSave}
                                >
                                    {getText('msg_efc007a393f6')}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Upload From Device */}
            {isUploadFromDeviceChecked && (
                <div style={overlayBackground}>
                    <div
                        style={overlayContainer}
                        className="card shadow-sm border-light"
                    >
                        <p
                            style={{
                                fontSize: '18px',
                                fontWeight: '600',
                                marginBottom: '30px',
                            }}
                        >
                            {getText('msg_0625a2d6e606')}
                        </p>
                        <div style={{ width: '100%', paddingTop: '6px' }}>
                            <FileDrop file={file} onChange={handleFileChange} />
                        </div>

                        <InputField
                            label={getText('msg_709a23220f2c')}
                            onChange={value =>
                                setAsset(updateAsset(asset, 'name', value))
                            }
                            maxChars={256}
                            required={true}
                            placeholder={getText('msg_b7ffb9789f09')}
                            value={asset.name}
                        />

                        <div style={{ marginBottom: '24px' }}>
                            <div className="file-form-label">
                                {getText('msg_848eed0fbd54')}
                                <Tooltip
                                    html="Use relevant and specific keywords to help make searching for an asset easier."
                                    placement="right"
                                >
                                    <i
                                        className="bi bi-info-circle"
                                        style={{
                                            paddingLeft: '5px',
                                            fontSize: '13px',
                                            color: 'var(--iNGAGED-Primary-Color)',
                                        }}
                                    ></i>
                                </Tooltip>
                            </div>
                            <TagInput
                                onTagsChange={tags =>
                                    setAsset(
                                        updateAsset(asset, 'assetTags', tags),
                                    )
                                }
                                savedTags={asset.tags}
                            />
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <SwitchField
                                label={getText('msg_0c359a10ae17')}
                                onChange={isChecked =>
                                    setAsset(
                                        updateAsset(
                                            asset,
                                            'assetTypeId',
                                            isChecked ? 1 : 2,
                                        ),
                                    )
                                }
                                tooltip={getText('msg_77453f9dd036')}
                                checked={asset.assetTypeId !== 2}
                            />
                        </div>

                        <div
                            id="footer"
                            style={{
                                height: 'fit-content',
                                width: '100%',
                                padding: '28px 28px 0',
                                display: isUploadFromDeviceChecked
                                    ? 'block'
                                    : 'none',
                            }}
                        >
                            <div
                                type="button"
                                style={{
                                    height: '40px',
                                    width: '128px',
                                    textAlign: 'center',
                                    display: 'inline-block',
                                }}
                                className="float-start button-base button-secondary"
                                onClick={() => setIsUploadFromDeviceChecked('')}
                            >
                                {getText('msg_77dfd2135f4d')}
                            </div>
                            <div
                                type="button"
                                style={{
                                    height: '40px',
                                    width: '128px',
                                    textAlign: 'center',
                                    display: 'inline-block',
                                }}
                                className="float-end button-base ing-button-primary-left"
                                onClick={() => uploadAsset()}
                            >
                                {getText('msg_61cc55aa0453')}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add From Assets */}
            {isAddFromAssetsChecked && (
                <div style={overlayBackground}>
                    <div
                        style={overlayContainer}
                        className="card shadow-sm border-light"
                    >
                        <div
                            className={styles.searchWrapper}
                            style={{ marginBottom: '26px' }}
                        >
                            <input
                                type="text"
                                className={styles.searchInput}
                                style={{ width: '100%' }}
                                placeholder={getText('msg_b18cdf8f03e9')}
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                            <span className={styles.searchIcon}>
                                <i className="bi bi-search"></i>
                            </span>
                        </div>

                        <AssetGridView
                            assets={assets}
                            propertiesModel={true}
                            handleClick={handleClick}
                            handleDoubleClick={() => null}
                            activeCard={activeCard}
                            fullAssetName={false}
                        />

                        {assets.length < 1 && (
                            <NotFoundBox
                                backgroundColor="#f0f4ff"
                                title={
                                    searchTerm
                                        ? 'Sorry, no results found'
                                        : 'Sorry, there are no assets to display'
                                }
                                description={
                                    searchTerm
                                        ? 'Try adjusting your search to find what you are looking for'
                                        : `You can upload a New Asset by clicking "Cancel" on the bottom left corner, then clicking on "Upload from Device"`
                                }
                            />
                        )}

                        <PaginationComponent
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />

                        <div
                            id="footer"
                            style={{
                                height: 'fit-content',
                                width: '100%',
                                padding: '28px 28px 0',
                                display: isAddFromAssetsChecked
                                    ? 'block'
                                    : 'none',
                            }}
                        >
                            <div
                                type="button"
                                style={{
                                    height: '40px',
                                    width: '128px',
                                    textAlign: 'center',
                                    display: 'inline-block',
                                }}
                                className="float-start button-base button-secondary"
                                onClick={() => setIsAddFromAssetsChecked('')}
                            >
                                {getText('msg_77dfd2135f4d')}
                            </div>
                            <div
                                type="button"
                                style={{
                                    height: '40px',
                                    width: '128px',
                                    textAlign: 'center',
                                    display: 'inline-block',
                                }}
                                className="float-end button-base ing-button-primary-left"
                                onClick={() => addAsset()}
                            >
                                {getText('msg_61cc55aa0453')}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientSettingBenefits;
